import { NextPage } from 'next';
import React, { FC } from 'react';
import { useIntl } from 'react-intl';
import { MerchantDetail } from '../../../../interfaces/merchants';
import { CategoryEntity } from '../../../../interfaces/explore/CategoryEntity';
import {
  allCategoriesCache,
  getCategory,
} from '../../../../infrastructure/api/backend/explore_api';
import { fetchMerchantDetail } from '../../../../infrastructure/api';
import ErrorTemplate from '../../../../templates/ErrorTemplate';
import JsonLd from '../../../../components/layouts/JsonLd';
import MetaTags from '../../../../components/layouts/MetaTags';
import { MerchantSite } from '../../../../components/MerchantSite';
import CouponDetailTemplate from '../../../../templates/merchants/coupons/DetailTemplate';
import { injectAppContextGetServerSideProps } from '../../../_app';
import {
  fetchCouponDetailForSSR,
  MerchantVoucher,
} from '../../../../infrastructure/api/coupon';
import { usePushMerchantDataLayerEffect } from '../../../../hooks/gtm';
import { buildSiteUrl } from 'src/utils/request-url';

type Props = {
  merchant?: MerchantDetail;
  coupon?: MerchantVoucher;
  category?: CategoryEntity;
  isUnpublishedSite: boolean;
  isCoubicApp: boolean;
};

const CouponDetail: FC<Props> = ({
  merchant,
  coupon,
  category,
  isCoubicApp,
}) => {
  const intl = useIntl();
  usePushMerchantDataLayerEffect({ visit_merchant_id: merchant?.id });

  if (merchant && category) {
    return (
      <>
        <MetaTags
          title={intl.formatMessage(
            { id: 'coupons.index.meta.title' },
            { name: merchant.display_name },
          )}
          description={
            merchant.seo_setting?.description || merchant.description
          }
          keywords={merchant.seo_setting?.keyword || ''}
          thumbnailUrl={merchant.cover_image.url || ''}
          currentPath={buildSiteUrl(`/coupons/${coupon?.public_id}`)}
          noIndex={merchant.seo_setting?.no_index}
          googleAnalyticsFourTrackingId={
            merchant.available_google_analytics_four_tracking_id
          }
          faviconImageUrl={merchant.favicon_image?.url}
        />
        <JsonLd merchant={merchant} />
        {coupon && (
          <CouponDetailTemplate
            merchant={merchant}
            coupon={{
              name: coupon.name,
              publicId: coupon.public_id,
              assignedBookingPages: coupon.assigned_booking_pages,
            }}
            isCoubicApp={isCoubicApp}
          />
        )}
      </>
    );
  }
  return <ErrorTemplate statusCode={404} />;
};

const MerchantCouponDetail: NextPage<Props> = (props: Props) => {
  const { merchant, isUnpublishedSite } = props;

  return (
    <MerchantSite
      isUnPublished={isUnpublishedSite}
      isMerchantInfoExist={!!merchant}
      logoImage={merchant?.logo_image || null}
      merchantPublicId={merchant?.public_id || null}
      displayName={merchant?.display_name || null}
      isCoubicApp={props.isCoubicApp}
    >
      <CouponDetail {...props} />
    </MerchantSite>
  );
};

export const getServerSideProps = injectAppContextGetServerSideProps<Props>(
  async (ctx) => {
    const { query, requestId, session, token, isCoubicApp } = ctx;
    const merchantPublicId = String(query.publicId);
    const couponPublicId = String(query.coupon_id);
    const merchantDetailResponse = await fetchMerchantDetail({
      session,
      token,
      requestId,
      merchantPublicId,
      serverResponse: ctx.res,
    });
    if (merchantDetailResponse.redirect) {
      return { redirect: merchantDetailResponse.redirect };
    }
    const { merchant, isUnpublishedSite } = merchantDetailResponse;

    if (isUnpublishedSite) {
      return {
        props: {
          merchant,
          isUnpublishedSite,
          categories: [],
          couponPublicId,
          isCoubicApp,
          isV2Flow: false,
        },
      };
    }

    const [coupon, categories] = await Promise.all([
      fetchCouponDetailForSSR({
        session,
        token,
        requestId,
        merchantPublicId,
        couponPublicId,
      }),
      allCategoriesCache(),
    ]);

    const category = merchant
      ? await getCategory(merchant.category_id)
      : undefined;
    return {
      props: {
        merchant,
        coupon,
        isUnpublishedSite,
        categories,
        category,
        couponPublicId,
        isCoubicApp,
      },
    };
  },
);

export default MerchantCouponDetail;
